import { Delete, Info, Warning } from '@mui/icons-material';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  LinearProgress,
  Stack,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import type {
  ConfirmModalVariant,
  IConfirmModalVariantConfig,
} from '@/@types/confirm-modal';
import { Modal } from '../Modal';

const confirmModalVariantConfig: Record<
  ConfirmModalVariant,
  IConfirmModalVariantConfig
> = {
  info: {
    Icon: Info,
    color: 'info',
  },
  warning: {
    Icon: Warning,
    color: 'warning',
  },
  error: {
    Icon: Delete,
    color: 'error',
  },
};

interface IConfirmModalProps {
  isOpen: boolean;
  isLoading: boolean;
  variant?: ConfirmModalVariant;
  title?: string;
  message?: string;
  cancelButtonText?: string;
  confirmButtonText?: string;
  onClose: () => void;
  onConfirm: () => void;
}

export function ConfirmModal({
  isOpen,
  isLoading,
  variant = 'warning',
  onClose,
  title,
  message = 'Você tem certeza que deseja realizar está ação?',
  cancelButtonText = 'Cancelar',
  confirmButtonText = 'Confirmar',
  onConfirm,
}: IConfirmModalProps) {
  const { Icon, color } = confirmModalVariantConfig[variant];

  return (
    <Modal open={isOpen} onClose={onClose} fullWidth maxWidth="xs">
      {isLoading ? <LinearProgress color={color} /> : null}
      <DialogContent sx={{ p: 2 }}>
        <Stack alignItems="center" justifyContent="center">
          <Box
            sx={(theme) => ({
              alignItems: 'center',
              bgcolor: alpha(theme.palette[color].main, 0.16),
              borderRadius: '50%',
              color: theme.palette[color].main,
              display: 'flex',
              height: 48,
              justifyContent: 'center',
              mb: 2,
              width: 48,
            })}
          >
            <Icon color="inherit" fontSize="medium" />
          </Box>
          {title ? (
            <DialogContentText variant="h5" fontWeight="bold" mb={1}>
              {title}
            </DialogContentText>
          ) : null}
          <DialogContentText sx={{ textAlign: 'center' }}>
            {message}
          </DialogContentText>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2, justifyContent: 'center' }}>
        <Button onClick={onClose} disabled={isLoading} autoFocus>
          {cancelButtonText}
        </Button>
        <Button
          variant="contained"
          color={color}
          onClick={onConfirm}
          disabled={isLoading}
        >
          {confirmButtonText}
        </Button>
      </DialogActions>
    </Modal>
  );
}
